import Page from "@/containers/Page";
import { useDonors } from "@/hooks/useDonors";
import { Donors } from "@/types/models/Donors";
import { PaginationButton } from "@/components/Table";
import { currency } from "@/lib/format";
import { usePageInfo } from "@/hooks/usePageInfo";
import { useRouter } from "next/router";
import { Col, Row } from "@/styles/layout";
import { downloadDonors } from "@/services/donors.service";
import { generateFilenameWithDateTime } from "@/lib/utils";
import { useCallback, useState } from "react";
import Input from "@/components/Input";
import {
  GoChevronLeft,
  GoChevronRight,
  GoTriangleDown,
  GoTriangleUp,
} from "react-icons/go";
import Loader from "../../components/Loader";
import { DonationsRequest } from "../../services/donations.service";
import { usePreloadDonor } from "@/hooks/useDonor";

export default function DonorsPage() {
  const router = useRouter();
  const [keyword, setKeyword] = useState<string | undefined>();
  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const {
    donors,
    page,
    pages,
    next,
    previous,
    canNext,
    canPrevious,
    total,
    isLoading,
    preloadData,
  } = useDonors({
    keyword,
    initialPage: 0,
    limit: 8,
    sortBy,
    sortDirection,
  });

  const { preloadDonor } = usePreloadDonor();

  const routeInfo = usePageInfo();

  const handleDonorClick = (donor: Donors) => {
    console.log(donor);
    router.push(`/donors/${donor.id}`);
  };

  const handleDownload = async () => {
    const content = await downloadDonors();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([content], { type: "text/csv" }));
    a.download = generateFilenameWithDateTime("donors", "csv");
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return {} as any;
  };

  return (
    <Page
      title={`${routeInfo.name} | GoGive`}
      header={`${routeInfo.name!} ${!isLoading ? `(${total})` : ""}`}
      Icon={routeInfo.icon!}
    >
      <Col>
        <Row justifyContent={"flex-start"} mb="16px">
          <DebouncedFilter onChange={setKeyword} />
        </Row>
      </Col>
      <div className="bg-white rounded-sm p-3">
        <table className="min-w-full text-left text-sm font-light">
          <thead className="border-b font-medium dark:border-neutral-500">
            <tr className="pb-4 ">
              <th className="px-6 py-4 font-normal text-gray-500">
                <HeaderSort
                  header="Since"
                  sortByField="createdAt"
                  onSort={(sortBy, order) => {
                    setSortBy(sortBy);
                    setSortDirection(order);
                  }}
                />
              </th>
              <th className="px-6 py-4 font-normal text-gray-500">Name</th>
              <th className="px-6 py-4 font-normal text-gray-500">Email</th>
              <th className="px-6 py-4 font-normal text-gray-500">
                Donations
                {/* <HeaderSort
                  header="Donations"
                  sortByField="donationCount"
                  onSort={(sortBy, order) => {
                    setSortBy(sortBy);
                    setSortDirection(order);
                  }}
                /> */}
              </th>
              <th className="px-6 py-4 font-normal text-gray-500">
                Donated
                {/* <HeaderSort
                  header="Donated"
                  sortByField="donatedGross"
                  onSort={(sortBy, order) => {
                    setSortBy(sortBy);
                    setSortDirection(order);
                  }}
                /> */}
              </th>
            </tr>
          </thead>
          <tbody>
            {donors.map((donor) => (
              <tr
                key={donor.id}
                onClick={() => handleDonorClick(donor)}
                onMouseEnter={() => preloadDonor(donor.id)}
                className="border-b dark:border-neutral-500 last:border-b-0 hover:bg-pink-50  transition-all ease-in-out duration-200 hover:cursor-pointer"
              >
                <td className="whitespace-nowrap px-1 md:px-6 py-5 font-normal text-sm md:text-md text-gray-600 ">
                  {new Date(donor.createdAt).toLocaleString()}
                </td>
                <td className="whitespace-nowrap px-1 md:px-6 py-5 font-semibold text-sm md:text-md text-gray-600 ">
                  {donor.company && (
                    <div className="text-gray-500 font-normal">
                      {donor.company}
                    </div>
                  )}
                  {`${donor.firstName} ${donor.lastName}`}
                </td>
                <td className="whitespace-nowrap px-1 md:px-6 py-5 font-normal text-sm md:text-md text-gray-600 ">
                  {`${donor.email} `}
                </td>
                <td className="whitespace-nowrap px-1 md:px-6 py-5 font-normal text-sm md:text-md text-gray-600 ">
                  {donor?.donations?.length ?? 0}
                </td>
                <td className="whitespace-nowrap px-1 md:px-6 py-5 font-semibold text-sm md:text-md text-gray-600 ">
                  {currency(
                    (donor.donations ?? []).reduce(
                      (total, donation) =>
                        total + donation.grossAmount - donation.gogiveAmount,
                      0
                    ) / 100
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {total > 0 || page > 1 ? (
          <>
            {previous && next && (pages ?? 0) > 1 && (
              <div className="flex justify-end items-center space-x-4">
                <div className="text-sm">{`Page ${page} of ${pages}`}</div>
                <div className="flex ">
                  <PaginationButton
                    onClick={previous}
                    Icon={GoChevronLeft}
                    disabled={!canPrevious}
                    onHover={() => preloadData(-1)}
                  />
                  <PaginationButton
                    onClick={next}
                    Icon={GoChevronRight}
                    disabled={!canNext}
                    onHover={() => preloadData(1)}
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="py-11 text-center text-md text-gray-500">
            {isLoading ? (
              <Loader size={24} />
            ) : (
              <> There are no donations to display yet</>
            )}
          </div>
        )}
      </div>
    </Page>
  );
}
const DebouncedFilter = ({ onChange }: { onChange: (e: string) => void }) => {
  const [keyword, setKeyword] = useState("");
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>();

  const handleInputChange = useCallback(
    (input: string) => {
      clearTimeout(timeoutId);

      // Create a new timeout to update the keyword after 500 milliseconds
      setKeyword(input);
      const timeId = setTimeout(() => {
        onChange(input);
      }, 500);
      setTimeoutId(timeId);
    },
    [onChange, timeoutId]
  );

  return (
    <Input
      label=""
      onChange={handleInputChange}
      placeholder="Find donors"
      containerStyle={{
        width: 300,
      }}
      value={keyword}
    />
  );
};

const HeaderSort = ({
  header,
  sortByField,
  onSort,
}: {
  header: string;
  sortByField: DonationsRequest["sortBy"];
  onSort: (
    sortBy: DonationsRequest["sortBy"],
    order: DonationsRequest["sortDirection"]
  ) => void;
}) => {
  const [order, setOrder] = useState<DonationsRequest["sortDirection"]>("DESC");
  return (
    <div
      className="flex items-center hover:cursor-pointer hover:text-pink-600"
      onClick={() => {
        const newOrder = order === "ASC" ? "DESC" : "ASC";
        onSort(sortByField, newOrder);
        setOrder(newOrder);
      }}
    >
      {header}
      <div className="ml-1">
        {order === "DESC" ? (
          <GoTriangleDown size={12} />
        ) : (
          <GoTriangleUp size={12} />
        )}
      </div>
    </div>
  );
};
